import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Event as EventIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { patientService, Patient } from '../services/patientService';
import { meetingService, Meeting } from '../services/meetingService';
import { formatDate, formatTime } from '../utils/dateUtils';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant, refreshKey } = useTenant();
  const navigate = useNavigate();

  // State for real data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  // Stats computed from real data
  const stats = {
    totalPatients: patients.length,
    activePatients: patients.filter(p => p.status === 'active').length,
    activeMeetings: meetings.filter(m => m.status === 'active').length,
    todayMeetings: meetings.filter(m => {
      const today = new Date().toDateString();
      const meetingDate = new Date(m.scheduled_at).toDateString();
      return meetingDate === today;
    }).length,
    completedMeetings: meetings.filter(m => m.status === 'done').length,
  };

  // Recent meetings from real data
  const recentMeetings = meetings
    .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime())
    .slice(0, 5);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user || !currentTenant) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load patients and meetings in parallel
        const [patientsData, meetingsData] = await Promise.all([
          patientService.getPatients(currentTenant.id),
          meetingService.getMeetings(currentTenant.id),
        ]);

        setPatients(patientsData);
        setMeetings(meetingsData);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user, currentTenant, refreshKey]);

  // Menu handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, meeting: Meeting) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedMeeting(meeting);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMeeting(null);
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>, meeting: Meeting) => {
    event.stopPropagation();
    setStatusAnchorEl(event.currentTarget);
    setSelectedMeeting(meeting);
  };

  const handleStatusMenuClose = () => {
    setStatusAnchorEl(null);
    setSelectedMeeting(null);
  };

  const handleViewDetails = () => {
    if (selectedMeeting) {
      navigate(`/meetings/${selectedMeeting.id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedMeeting) {
      navigate(`/meetings?edit=${selectedMeeting.id}`);
    }
    handleMenuClose();
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!selectedMeeting) return;

    try {
      await meetingService.updateMeeting(selectedMeeting.id, { status: newStatus as any });

      // Update local state
      setMeetings(meetings.map(m =>
        m.id === selectedMeeting.id
          ? { ...m, status: newStatus as any }
          : m
      ));
    } catch (error) {
      console.error('Error updating meeting status:', error);
    }
    handleStatusMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'done':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'noshow':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading dashboard data: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Welcome back, {user?.first_name}!
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {user?.role === 'admin' ? 'System Administrator' : 'Therapist Dashboard'}
        {currentTenant && ` - ${currentTenant.name}`}
      </Typography>

      {!currentTenant && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {user?.role === 'admin'
            ? 'No tenant selected. Please select a tenant from the top bar.'
            : 'No tenant assigned. Please contact your administrator.'
          }
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Patients
              </Typography>
              <Typography variant="h5" component="div">
                {stats.totalPatients}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Patients
              </Typography>
              <Typography variant="h5" component="div">
                {stats.activePatients}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today's Meetings
              </Typography>
              <Typography variant="h5" component="div">
                {stats.todayMeetings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed Meetings
              </Typography>
              <Typography variant="h5" component="div">
                {stats.completedMeetings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/patients')}
                >
                  Manage Patients
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/meetings')}
                >
                  Schedule Meeting
                </Button>
                {user?.role === 'admin' && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/users')}
                  >
                    Manage Users
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Meetings
              </Typography>
              <List dense>
                {recentMeetings.length > 0 ? (
                  recentMeetings.map((meeting) => (
                    <ListItem
                      key={meeting.id}
                      secondaryAction={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={meeting.status.toUpperCase()}
                            color={getStatusColor(meeting.status) as any}
                            size="small"
                            onClick={(e) => handleStatusClick(e, meeting)}
                            sx={{ cursor: 'pointer' }}
                          />
                          <IconButton
                            edge="end"
                            onClick={(e) => handleMenuClick(e, meeting)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Button
                            variant="text"
                            sx={{
                              p: 0,
                              textAlign: 'left',
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => navigate(`/meetings/${meeting.id}`)}
                          >
                            {meeting.patient.full_name}
                          </Button>
                        }
                        secondary={`${formatDate(meeting.scheduled_at)} at ${formatTime(meeting.scheduled_at)}`}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No meetings yet"
                      secondary="Create your first meeting to see it here"
                    />
                  </ListItem>
                )}
              </List>
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/meetings')}
                sx={{ mt: 1 }}
              >
                View All Meetings
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Meeting Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <EventIcon sx={{ mr: 2 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 2 }} />
          Edit
        </MenuItem>
      </Menu>

      {/* Status Menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusMenuClose}
      >
        {selectedMeeting?.status === 'active' && (
          <>
            <MenuItem onClick={() => handleUpdateStatus('done')}>Mark as Done</MenuItem>
            <MenuItem onClick={() => handleUpdateStatus('cancelled')}>Cancel</MenuItem>
            <MenuItem onClick={() => handleUpdateStatus('noshow')}>Mark as No-Show</MenuItem>
          </>
        )}
        {(selectedMeeting?.status === 'noshow' || selectedMeeting?.status === 'cancelled' || selectedMeeting?.status === 'done') && (
          <MenuItem onClick={() => handleUpdateStatus('active')}>Reactivate</MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default DashboardPage;